import React, { useState, useCallback } from 'react';
import { Task, TaskStatus, Note, OrgMember } from '@shared/types';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@shared/components/ui/command';
import { RichTextEditor } from '@shared/components/note/RichTextEditor';
import { cn } from '@shared/lib/utils';
import {
  Archive, ArchiveRestore, Trash2, Plus, X, CalendarDays, Link2, FileText, Check, ChevronDown
} from 'lucide-react';
import {
  useCreateTaskMutation,
  useUpdateTaskMutation, useDeleteTaskMutation,
  useAddAssigneeMutation, useRemoveAssigneeMutation,
  useAddTaskLinkMutation, useRemoveTaskLinkMutation,
} from '@shared/hooks/useTasksQuery';
import { format, parseISO } from 'date-fns';

const STATUS_OPTIONS: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'TODO', label: 'To-do', color: 'bg-slate-400' },
  { value: 'IN_PROGRESS', label: 'In progress', color: 'bg-blue-500' },
  { value: 'IN_REVIEW', label: 'In review', color: 'bg-yellow-500' },
  { value: 'DONE', label: 'Done', color: 'bg-green-500' },
];

interface TaskDetailSlideOverProps {
  task: Task | null;
  open: boolean;
  onClose: () => void;
  allTasks: Task[];
  notes?: Note[];
  orgMembers?: OrgMember[];
}

export function TaskDetailSlideOver({
  task, open, onClose, allTasks, notes = [], orgMembers = [],
}: TaskDetailSlideOverProps) {
  const createTask = useCreateTaskMutation();
  const updateTask = useUpdateTaskMutation();
  const deleteTask = useDeleteTaskMutation();
  const addAssignee = useAddAssigneeMutation();
  const removeAssignee = useRemoveAssigneeMutation();
  const addLink = useAddTaskLinkMutation();
  const removeLink = useRemoveTaskLinkMutation();

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [subtaskInput, setSubtaskInput] = useState('');
  const [showSubtaskInput, setShowSubtaskInput] = useState(false);

  // Declared before the early return so the hook order stays stable when the
  // `task` prop toggles between null and a value (otherwise React throws
  // "Rendered more hooks than during the previous render").
  const handleDescriptionChange = useCallback((value: object) => {
    if (!task) return;
    updateTask.mutate({ taskId: task.task_id, payload: { description: value } });
  }, [task?.task_id]);

  if (!open || !task) return null;

  const handleStatusChange = (status: TaskStatus) => {
    updateTask.mutate({ taskId: task.task_id, payload: { status } });
  };

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== task.title) {
      updateTask.mutate({ taskId: task.task_id, payload: { title: titleValue } });
    }
    setEditingTitle(false);
  };

  const handleToggleArchive = () => {
    const next = !task.is_archived;
    updateTask.mutate({ taskId: task.task_id, payload: { is_archived: next } });
    // Archiving hides the task from the default board, so close the panel;
    // unarchiving keeps it open since it's now active again.
    if (next) onClose();
  };

  const handleDelete = () => {
    deleteTask.mutate(task.task_id);
    onClose();
  };

  const handleDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateTask.mutate({ taskId: task.task_id, payload: { due_date: e.target.value } });
  };

  const handleLinkNote = (noteId: string) => {
    updateTask.mutate({ taskId: task.task_id, payload: { note_id: noteId } });
  };

  // Create a brand-new task (inheriting the parent's status) and link it as a
  // subtask of the current task. Keeps the input open for rapid entry.
  const handleCreateSubtask = async () => {
    const title = subtaskInput.trim();
    if (!task || !title || createTask.isPending) return;
    const created = await createTask.mutateAsync({
      title,
      status: task.status,
      org_id: task.org_id ?? undefined,
    });
    await addLink.mutateAsync({ taskId: task.task_id, linkedTaskId: created.task_id, linkType: 'SUBTASK' });
    setSubtaskInput('');
  };

  const subtasks = task.links?.filter(l => l.link_type === 'SUBTASK') ?? [];
  const relatedTasks = task.links?.filter(l => l.link_type === 'RELATED') ?? [];
  const assignees = task.assignees ?? [];

  const unassignedMembers = orgMembers.filter(
    m => !assignees.some(a => a.user_id === m.user_id)
  );

  const linkableTasks = allTasks.filter(
    t => t.task_id !== task.task_id &&
    !task.links?.some(l => l.linked_task_id === t.task_id)
  );

  return (
    // Non-modal fixed side panel (like the notes panel): the board stays
    // interactive and shrinks beside it, rather than being covered by a modal.
    <div className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[540px] bg-background border-l border-border shadow-lg flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-start justify-between gap-3">
            {editingTitle ? (
              <Input
                autoFocus
                value={titleValue}
                onChange={e => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={e => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') setEditingTitle(false); }}
                className="text-lg font-semibold border-none p-0 h-auto focus-visible:ring-0 shadow-none"
              />
            ) : (
              <h2
                className="text-lg font-semibold cursor-pointer hover:text-primary transition-colors leading-snug"
                onClick={() => { setTitleValue(task.title); setEditingTitle(true); }}
              >
                {task.title}
              </h2>
            )}
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost" size="icon" className="h-8 w-8"
                onClick={handleToggleArchive}
                title={task.is_archived ? 'Unarchive' : 'Archive'}
              >
                {task.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={handleDelete} title="Delete">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} title="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Status + Due date row */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Select value={task.status} onValueChange={v => handleStatusChange(v as TaskStatus)}>
              <SelectTrigger className="h-7 w-auto gap-1.5 text-xs border-none bg-accent px-2 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>
                    <div className="flex items-center gap-2">
                      <span className={cn('h-2 w-2 rounded-full', s.color)} />
                      {s.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="date"
                className="text-xs bg-transparent border-none outline-none text-muted-foreground cursor-pointer"
                value={task.due_date ? task.due_date.substring(0, 10) : ''}
                onChange={handleDueDateChange}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col">
          {/* Description — grows to fill so the metadata sits at the bottom */}
          <div className="px-6 py-4 border-b border-border flex-1 flex flex-col">
            <RichTextEditor
              value={task.description ?? ''}
              onChange={handleDescriptionChange}
              noteId={task.task_id}
              placeholder="Add a description..."
              className="flex flex-col flex-1 min-h-[160px]"
            />
          </div>

          {/* Assignees appear once the user belongs to an org with members. */}
          {orgMembers.length > 0 && (
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assignees</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-56 pointer-events-auto z-[60]">
                  <Command>
                    <CommandInput placeholder="Search members..." />
                    <CommandEmpty>No members found</CommandEmpty>
                    <CommandGroup>
                      {unassignedMembers.map(m => (
                        <CommandItem
                          key={m.user_id}
                          onSelect={() => addAssignee.mutate({ taskId: task.task_id, userId: m.user_id })}
                        >
                          <span className="truncate">{m.user?.name ?? m.user_id}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-wrap gap-2">
              {assignees.map(a => (
                <div key={a.user_id} className="flex items-center gap-1.5 bg-accent rounded-full pl-1 pr-2 py-0.5">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                      {(a.user?.name ?? a.user_id).charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs">{a.user?.name ?? a.user_id}</span>
                  <button onClick={() => removeAssignee.mutate({ taskId: task.task_id, userId: a.user_id })} className="text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {assignees.length === 0 && (
                <span className="text-xs text-muted-foreground">No assignees</span>
              )}
            </div>
          </div>
          )}

          {/* Linked note */}
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Linked Note</span>
              {!task.note_id && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-64 pointer-events-auto z-[60]">
                    <Command>
                      <CommandInput placeholder="Search notes..." />
                      <CommandEmpty>No notes found</CommandEmpty>
                      <CommandGroup>
                        {notes.map(n => (
                          <CommandItem key={n.note_id} onSelect={() => handleLinkNote(n.note_id)}>
                            <FileText className="h-3.5 w-3.5 mr-2 shrink-0" />
                            <span className="truncate">{n.title || 'Untitled'}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            {task.note_id ? (
              <div className="flex items-center justify-between bg-accent rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm truncate">
                    {task.linked_note?.title ?? notes.find(n => n.note_id === task.note_id)?.title ?? 'Note'}
                  </span>
                </div>
                <button
                  onClick={() => updateTask.mutate({ taskId: task.task_id, payload: { note_id: '' } })}
                  className="text-muted-foreground hover:text-foreground ml-2"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">No linked note</span>
            )}
          </div>

          {/* Subtasks */}
          <div className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Subtasks {subtasks.length > 0 && `(${subtasks.length})`}
              </span>
              <Button
                variant="ghost" size="icon" className="h-6 w-6"
                onClick={() => setShowSubtaskInput(v => !v)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-1">
              {subtasks.map(link => {
                // Resolve the current task (live) — link.linked_task is a snapshot
                // that can go stale after the subtask's status changes.
                const linked = allTasks.find(t => t.task_id === link.linked_task_id) ?? link.linked_task;
                return (
                  <div key={link.task_link_id} className="flex items-center justify-between group py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        linked?.status === 'DONE' ? 'bg-green-500' : 'bg-slate-400'
                      )} />
                      <span className={cn(
                        'text-sm truncate',
                        linked?.status === 'DONE' && 'line-through text-muted-foreground'
                      )}>
                        {linked?.title ?? link.linked_task_id}
                      </span>
                    </div>
                    <button
                      onClick={() => removeLink.mutate({ taskId: task.task_id, linkId: link.task_link_id })}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {subtasks.length === 0 && !showSubtaskInput && (
                <span className="text-xs text-muted-foreground">No subtasks</span>
              )}
              {showSubtaskInput && (
                <Input
                  autoFocus
                  value={subtaskInput}
                  onChange={e => setSubtaskInput(e.target.value)}
                  placeholder="Subtask title, press Enter…"
                  className="h-8 text-sm mt-1"
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreateSubtask();
                    if (e.key === 'Escape') { setShowSubtaskInput(false); setSubtaskInput(''); }
                  }}
                  onBlur={() => { if (!subtaskInput.trim()) setShowSubtaskInput(false); }}
                />
              )}
            </div>
          </div>

          {/* Related tasks */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Related Tasks {relatedTasks.length > 0 && `(${relatedTasks.length})`}
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-64 pointer-events-auto z-[60]">
                  <Command>
                    <CommandInput placeholder="Link a task..." />
                    <CommandEmpty>No tasks found</CommandEmpty>
                    <CommandGroup>
                      {linkableTasks.map(t => (
                        <CommandItem
                          key={t.task_id}
                          onSelect={() => addLink.mutate({ taskId: task.task_id, linkedTaskId: t.task_id, linkType: 'RELATED' })}
                        >
                          <span className="truncate">{t.title}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              {relatedTasks.map(link => {
                const linked = allTasks.find(t => t.task_id === link.linked_task_id) ?? link.linked_task;
                return (
                  <div key={link.task_link_id} className="flex items-center justify-between group py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate">
                        {linked?.title ?? link.linked_task_id}
                      </span>
                    </div>
                    <button
                      onClick={() => removeLink.mutate({ taskId: task.task_id, linkId: link.task_link_id })}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
              {relatedTasks.length === 0 && (
                <span className="text-xs text-muted-foreground">No related tasks</span>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}
